import {
  button_default
} from "./chunk.USNIHC23.js";
import {
  MirrorValidator
} from "./chunk.RH6KZZ6S.js";
import {
  er,
  ke
} from "./chunk.FK6RKZHR.js";
import {
  WaInvalidEvent,
  WebAwesomeFormAssociatedElement
} from "./chunk.SNETKOHC.js";
import {
  passthrough_default
} from "./chunk.NHSM3NIS.js";
import {
  to
} from "./chunk.EH2PNIGX.js";
import {
  size_default
} from "./chunk.SI24X5IH.js";
import {
  Rt
} from "./chunk.HBRIRAGU.js";
import {
  LocalizeController
} from "./chunk.ZPJRWEFM.js";
import {
  variants_default
} from "./chunk.7OG3CMFN.js";
import {
  appearance_default
} from "./chunk.GO6CI477.js";
import {
  watch
} from "./chunk.2NT6DI7B.js";
import {
  e,
  n,
  r,
  t
} from "./chunk.LVUMIIKQ.js";
import {
  __decorateClass
} from "./chunk.SZE6ONST.js";

// src/components/button/button.css
var button_default2 = ":host {\n  --display: inline-flex;\n\n  position: relative;\n}\n\n/*\n * Label\n */\n\n.prefix,\n.suffix {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n  pointer-events: none;\n}\n\n.label {\n  display: inline-block;\n}\n\n.label::slotted(wa-icon) {\n  vertical-align: -2px;\n}\n\n/*\n * Caret modifier\n */\n\nwa-icon[part~='caret'] {\n  display: flex;\n  align-self: center;\n  align-items: center;\n\n  &::part(svg) {\n    width: 0.875em;\n    height: 0.875em;\n  }\n\n  .wa-button:has(&) .suffix {\n    display: none;\n  }\n}\n\n/*\n * Loading modifier\n */\n\n.loading {\n  position: relative;\n  cursor: wait;\n\n  .prefix,\n  .label,\n  .suffix,\n  .caret {\n    visibility: hidden;\n  }\n\n  wa-spinner {\n    --indicator-color: currentColor;\n    --track-color: color-mix(in oklab, currentColor, transparent 90%);\n\n    position: absolute;\n    font-size: 1em;\n    height: 1em;\n    width: 1em;\n    top: calc(50% - 0.5em);\n    left: calc(50% - 0.5em);\n  }\n}\n\n/*\n * Badges\n */\n\nbutton ::slotted(wa-badge) {\n  border-color: var(--wa-color-surface-default);\n  position: absolute;\n  inset-block-start: 0;\n  inset-inline-end: 0;\n  translate: 50% -50%;\n  pointer-events: none;\n}\n\n:host(:dir(rtl)) ::slotted(wa-badge) {\n  translate: -50% -50%;\n}\n\n/*\n* Button spacing\n*/\n\nslot[name='prefix']::slotted(*) {\n  margin-inline-end: var(--wa-space);\n}\n\nslot[name='suffix']::slotted(*),\n.wa-button:not(.visually-hidden-label) [part~='caret'] {\n  margin-inline-start: var(--wa-space);\n}\n";

// src/components/button/button.ts
var WaButton = class extends WebAwesomeFormAssociatedElement {
  constructor() {
    super(...arguments);
    this.assumeInteractionOn = ["click"];
    this.localize = new LocalizeController(this);
    this.visuallyHiddenLabel = false;
    this.invalid = false;
    this.title = "";
    this.variant = "inherit";
    this.appearance = "accent";
    this.size = "inherit";
    this.caret = false;
    this.disabled = false;
    this.loading = false;
    this.pill = false;
    this.type = "button";
    this.name = null;
    this.value = null;
    this.href = null;
    this.form = null;
  }
  static get validators() {
    return [...super.validators, MirrorValidator()];
  }
  handleClick() {
    const form = this.getForm();
    if (!form) return;
    const lightDOMButton = this.constructLightDOMButton();
    this.parentElement?.append(lightDOMButton);
    lightDOMButton.click();
    lightDOMButton.remove();
  }
  constructLightDOMButton() {
    const button = document.createElement("button");
    button.type = this.type;
    button.style.position = "absolute";
    button.style.width = "0";
    button.style.height = "0";
    button.style.clipPath = "inset(50%)";
    button.style.overflow = "hidden";
    button.style.whiteSpace = "nowrap";
    if (this.name) {
      button.name = this.name;
    }
    button.value = this.value || "";
    ["form", "formaction", "formenctype", "formmethod", "formnovalidate", "formtarget"].forEach((attr) => {
      if (this.hasAttribute(attr)) {
        button.setAttribute(attr, this.getAttribute(attr));
      }
    });
    return button;
  }
  handleInvalid() {
    this.dispatchEvent(new WaInvalidEvent());
  }
  handleLabelSlotChange(event) {
    const elements = event.target.assignedElements({ flatten: true });
    if (elements.length === 1 && elements[0].localName === "wa-visually-hidden") {
      this.visuallyHiddenLabel = true;
    }
  }
  isButton() {
    return this.href ? false : true;
  }
  isLink() {
    return this.href ? true : false;
  }
  handleDisabledChange() {
    this.updateValidity();
  }
  // eslint-disable-next-line
  setValue(..._args) {
  }
  /** Simulates a click on the button. */
  click() {
    this.button.click();
  }
  /** Sets focus on the button. */
  focus(options) {
    this.button.focus(options);
  }
  /** Removes focus from the button. */
  blur() {
    this.button.blur();
  }
  render() {
    const isLink = this.isLink();
    const tag = isLink ? er`a` : er`button`;
    return ke`
      <${tag}
        part="base"
        class=${Rt({
      button: true,
      "wa-button": true,
      caret: this.caret,
      disabled: this.disabled,
      loading: this.loading,
      rtl: this.localize.dir() === "rtl",
      "visually-hidden-label": this.visuallyHiddenLabel
    })}
        ?disabled=${to(isLink ? void 0 : this.disabled)}
        type=${to(isLink ? void 0 : this.type)}
        title=${this.title}
        name=${to(isLink ? void 0 : this.name)}
        value=${to(isLink ? void 0 : this.value)}
        href=${to(isLink ? this.href : void 0)}
        target=${to(isLink ? this.target : void 0)}
        download=${to(isLink ? this.download : void 0)}
        rel=${to(isLink && this.rel ? this.rel : void 0)}
        role=${to(isLink ? void 0 : "button")}
        aria-disabled=${this.disabled ? "true" : "false"}
        tabindex=${this.disabled ? "-1" : "0"}
        @invalid=${this.isButton() ? this.handleInvalid : null}
        @click=${this.handleClick}
      >
        <slot name="prefix" part="prefix" class="prefix"></slot>
        <slot part="label" class="label" @slotchange=${this.handleLabelSlotChange}></slot>
        <slot name="suffix" part="suffix" class="suffix"></slot>
        ${this.caret ? ke`
                <wa-icon part="caret" class="caret" library="system" name="chevron-down" variant="solid"></wa-icon>
              ` : ""}
        ${this.loading ? ke`<wa-spinner part="spinner"></wa-spinner>` : ""}
      </${tag}>
    `;
  }
};
WaButton.shadowStyle = [passthrough_default, variants_default, appearance_default, size_default, button_default, button_default2];
WaButton.rectProxy = "button";
__decorateClass([
  e(".wa-button")
], WaButton.prototype, "button", 2);
__decorateClass([
  r()
], WaButton.prototype, "visuallyHiddenLabel", 2);
__decorateClass([
  r()
], WaButton.prototype, "invalid", 2);
__decorateClass([
  n()
], WaButton.prototype, "title", 2);
__decorateClass([
  n({ reflect: true, initial: "neutral" })
], WaButton.prototype, "variant", 2);
__decorateClass([
  n({ reflect: true, default: "accent" })
], WaButton.prototype, "appearance", 2);
__decorateClass([
  n({ reflect: true, initial: "medium" })
], WaButton.prototype, "size", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaButton.prototype, "caret", 2);
__decorateClass([
  n({ type: Boolean })
], WaButton.prototype, "disabled", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaButton.prototype, "loading", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaButton.prototype, "pill", 2);
__decorateClass([
  n()
], WaButton.prototype, "type", 2);
__decorateClass([
  n({ reflect: true })
], WaButton.prototype, "name", 2);
__decorateClass([
  n({ reflect: true })
], WaButton.prototype, "value", 2);
__decorateClass([
  n({ reflect: true })
], WaButton.prototype, "href", 2);
__decorateClass([
  n()
], WaButton.prototype, "target", 2);
__decorateClass([
  n()
], WaButton.prototype, "rel", 2);
__decorateClass([
  n()
], WaButton.prototype, "download", 2);
__decorateClass([
  n({ reflect: true })
], WaButton.prototype, "form", 2);
__decorateClass([
  n({ attribute: "formaction" })
], WaButton.prototype, "formAction", 2);
__decorateClass([
  n({ attribute: "formenctype" })
], WaButton.prototype, "formEnctype", 2);
__decorateClass([
  n({ attribute: "formmethod" })
], WaButton.prototype, "formMethod", 2);
__decorateClass([
  n({ attribute: "formnovalidate", type: Boolean })
], WaButton.prototype, "formNoValidate", 2);
__decorateClass([
  n({ attribute: "formtarget" })
], WaButton.prototype, "formTarget", 2);
__decorateClass([
  watch("disabled", { waitUntilFirstUpdate: true })
], WaButton.prototype, "handleDisabledChange", 1);
WaButton = __decorateClass([
  t("wa-button")
], WaButton);

export {
  WaButton
};
